import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Newsroom', path: '/newsroom' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Desktop spacing area */}
          <div className="hidden md:block w-48" />

          {/* Mobile logo shown only after scroll */}
          <div className="md:hidden flex-1 flex items-center">
            {isScrolled && (
              <Link
                to="/"
                className="inline-flex items-center pt-1"
                aria-label="Go to home"
                data-testid="header-mobile-scrolled-logo-link"
              >
                <img
                  src="/sumit-home-logo-optimized.webp"
                  alt="SUM-IT Corporate Services logo"
                  className="h-20 w-auto"
                  loading="lazy"
                  decoding="async"
                  data-testid="header-mobile-scrolled-logo-image"
                />
              </Link>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 relative group px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm ${
                  location.pathname === link.path
                    ? 'text-[#B19CD9]'
                    : 'text-white hover:text-[#B19CD9]'
                }`}
                data-testid={`header-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#B19CD9] transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              asChild
              className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
            >
              <Link to="/contact">Get Started</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden inline-flex h-11 w-11 items-center justify-center text-white hover:text-[#B19CD9] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-md"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
            data-testid="header-mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/98 backdrop-blur-lg border-t border-white/10">
          <nav id="mobile-navigation-menu" className="container mx-auto px-4 py-6 flex flex-col space-y-4" data-testid="header-mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-base font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm ${
                  location.pathname === link.path
                    ? 'text-[#B19CD9]'
                    : 'text-white hover:text-[#B19CD9]'
                }`}
                data-testid={`header-mobile-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            <Button
              asChild
              className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold w-full"
            >
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                Get Started
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
